"use client";

import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod/v4";
import Link from "next/link";
import { useActionState } from "react";

import { FORM_VALIDATE_OPTIONS, formConstraint } from "@/components/forms/conform-options";
import { FieldError, FormError } from "@/components/forms/form-error";
import { LabelRow } from "@/components/forms/label-row";
import { OAuthProviderButtons } from "@/components/forms/oauth-provider-buttons";
import { SubmitButton } from "@/components/forms/submit-button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import type { OAuthFormAction, OAuthProvider } from "@/lib/auth/oauth-providers";
import { loginSchema } from "@/lib/auth/schemas";
import { withNext } from "@/lib/auth/safe-next";

import { OAuthRecoveryNotice } from "./_oauth-recovery";
import { loginAction, loginWithAppleAction, loginWithGoogleAction } from "./actions";

/** Server Actions can't be looked up dynamically — each is its own module
 *  export — so the provider→action mapping is spelled out here and keyed by
 *  the same union the registry uses. A new provider fails to compile until
 *  it has an action. */
const ACTIONS: Record<OAuthProvider, OAuthFormAction> = {
  google: loginWithGoogleAction,
  apple: loginWithAppleAction,
};

const constraint = formConstraint(loginSchema);

type Props = {
  oauthError?: string;
  oauthErrorCode?: string;
  next?: string;
  /** Server-detected native-shell flag (lib/shell-server) — threaded as a
   *  prop so the recovery notice can vary copy without a hydration flash. */
  shell?: boolean;
};

export function LoginForm({ oauthError, oauthErrorCode, next, shell }: Props) {
  const [lastResult, action] = useActionState(loginAction, undefined);
  const [form, fields] = useForm({
    lastResult,
    constraint,
    ...FORM_VALIDATE_OPTIONS,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: loginSchema });
    },
  });

  return (
    <div className="space-y-4">
      <OAuthRecoveryNotice message={oauthError} code={oauthErrorCode} shell={shell} />

      <OAuthProviderButtons
        actions={ACTIONS}
        verb="Sign in"
        next={next}
        errorReturnPath={withNext("/login", next)}
        shell={shell}
      />

      <form {...getFormProps(form)} action={action} className="space-y-3">
        {next ? <input type="hidden" name="next" value={next} /> : null}
        <FormError id={form.errorId} message={form.errors?.[0]} />
        <div className="space-y-1.5">
          <LabelRow htmlFor={fields.email.id}>Email</LabelRow>
          <Input
            {...getInputProps(fields.email, { type: "email" })}
            autoComplete="email"
            placeholder="you@example.com"
          />
          <FieldError id={fields.email.errorId} message={fields.email.errors?.[0]} />
        </div>
        <div className="space-y-1.5">
          <LabelRow
            htmlFor={fields.password.id}
            trailing={
              <Link
                href="/auth/reset"
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                Forgot password?
              </Link>
            }
          >
            Password
          </LabelRow>
          <PasswordInput
            {...getInputProps(fields.password, { type: "password" })}
            autoComplete="current-password"
          />
          <FieldError id={fields.password.errorId} message={fields.password.errors?.[0]} />
        </div>
        <SubmitButton label="Sign in" />
      </form>
    </div>
  );
}
